/**
 * NameProjectFields — shared form fragment for the project naming convention.
 *
 * Renders three coupled inputs:
 *   • Project Number  (locked, PREVIEWED on mount, RESERVED at submit)
 *   • Client Label    (editable, defaults from caller)
 *   • Descriptor      (editable combobox: controlled list + free-text)
 *
 * Plus a live preview of the canonical display name
 * `{projectNumber} {clientLabel} {descriptor}`.
 *
 * Reservation flow (intentionally deferred):
 *   1. On mount in `create` mode the field peeks the next number via
 *      `getOrSetProjectNumberCounter` (read-only). The displayed number is a
 *      *preview*; the counter is NOT incremented.
 *   2. The parent modal calls `reserveProjectNumberOnSubmit()` from this
 *      module immediately before saving the new deal. That call invokes
 *      `assignProjectNumber` and is what actually advances the global
 *      monotonic counter. The returned number must be used in the write.
 *   3. If the operator cancels the modal before submit, no number is burned.
 *
 * This fragment is embedded into every project-creation modal so the
 * onboarding moment is identical wherever a new project is born:
 * QualifyLeadModal, ConvertToDealModal, ClientPortalManagement (Add project),
 * CopyProposalModal, etc.
 *
 * In edit mode the Project Number is read-only by definition (it's
 * immutable). Reuse this fragment from the rename modal too — pass
 * `mode="edit"` and the existing number.
 */

import React, { useEffect, useId, useMemo, useState } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Lock, Sparkles } from 'lucide-react';
import { DESCRIPTOR_SUGGESTIONS, isProjectNumber, normalizeDescriptor } from '../../utils/projectName';
import type { NameProjectFieldsValue } from '../../utils/projectName';
import './NameProjectFields.css';

export type { NameProjectFieldsValue };

/** Format a counter integer as a zero-padded `#NNNN`. Mirrors the server-side helper. */
function formatProjectNumberFromSeq(seq: number): string {
  return `#${String(Math.max(1, Math.floor(seq))).padStart(4, '0')}`;
}

/**
 * Reserve the next project number from the global counter. Parent modals
 * MUST call this immediately before persisting the new deal (i.e. inside the
 * submit handler, after validation). The returned number is the canonical
 * value to write to Firestore — do not use the previewed number from state,
 * since another user may have reserved between preview and submit.
 *
 * Throws on auth / network failure so the caller can surface the error and
 * abort the save without leaving an orphaned record.
 */
export async function reserveProjectNumberOnSubmit(): Promise<string> {
  const fn = httpsCallable<{ year?: number }, { projectNumber: string }>(
    getFunctions(),
    'assignProjectNumber',
  );
  const result = await fn({ year: new Date().getFullYear() });
  return result.data.projectNumber;
}

interface NameProjectFieldsProps {
  /**
   * "create" assigns a new Project Number on mount via the
   * `assignProjectNumber` Cloud Function. "edit" treats the supplied
   * `value.projectNumber` as locked and never re-fetches.
   */
  mode: 'create' | 'edit';
  /** Initial values. In create mode, `projectNumber` is ignored. */
  value: NameProjectFieldsValue;
  /** Called whenever any field changes (also called once after the project
   *  number is reserved in create mode). */
  onChange: (next: NameProjectFieldsValue) => void;
  /** Called once when the project number is successfully reserved (create
   *  mode) so the caller can disable Submit until then. */
  onReady?: (projectNumber: string) => void;
  /** Reports inflight assignment errors (Firebase Functions errors etc.). */
  onError?: (message: string) => void;
  /** Disables every field. Used while the parent modal is saving. */
  disabled?: boolean;
  /** When true in create mode, skip counter peek/reservation — number assigned later. */
  deferProjectNumber?: boolean;
  /** When true in edit mode without a number, preview the next number and reserve on submit. */
  assignProjectNumberOnSubmit?: boolean;
  /** Shown on the deferred-number row in edit mode (e.g. under-contract jobs). */
  assignNumberHint?: string;
  /**
   * Create mode, when the number is deferred. Defaults to the design-fee note
   * used by Add Project. Label and descriptor stay editable either way.
   */
  deferredNumberNote?: string;
  /** Edit mode: start assigning a number from this modal. */
  onAssignNumber?: () => void;
  /** Denser grid; hides long helper copy (tooltips on labels instead). */
  compact?: boolean;
}

export const NameProjectFields: React.FC<NameProjectFieldsProps> = ({
  mode,
  value,
  onChange,
  onReady,
  onError,
  disabled,
  deferProjectNumber = false,
  assignProjectNumberOnSubmit = false,
  assignNumberHint,
  deferredNumberNote,
  onAssignNumber,
  compact = false,
}) => {
  const skipNumber =
    (mode === 'create' && deferProjectNumber)
    || (mode === 'edit' && !assignProjectNumberOnSubmit && !isProjectNumber(value.projectNumber));
  const shouldPreviewNumber =
    (mode === 'create' && !deferProjectNumber)
    || (mode === 'edit' && assignProjectNumberOnSubmit && !isProjectNumber(value.projectNumber));
  const [reserving, setReserving] = useState(
    shouldPreviewNumber && !isProjectNumber(value.projectNumber),
  );
  const numberFieldId = useId();
  const labelFieldId = useId();
  const descriptorFieldId = useId();
  const datalistId = useId();

  // Peek the next project number on mount in create mode. The counter is
  // NOT advanced here — actual reservation happens at submit time via
  // `reserveProjectNumberOnSubmit()` so cancelled dialogs don't burn numbers.
  useEffect(() => {
    if (!shouldPreviewNumber) return;
    if (isProjectNumber(value.projectNumber)) return; // Already previewed/reserved
    let cancelled = false;
    setReserving(true);
    (async () => {
      try {
        const fn = httpsCallable<{}, { next: number; startBaseline: number }>(
          getFunctions(),
          'getOrSetProjectNumberCounter',
        );
        const result = await fn({});
        if (cancelled) return;
        const next = Number(result.data?.next || 0);
        const preview = formatProjectNumberFromSeq(next > 0 ? next : 1);
        onChange({ ...value, projectNumber: preview });
        onReady?.(preview);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to read the project-number counter.';
        console.error('[NameProjectFields] peek getOrSetProjectNumberCounter failed:', err);
        onError?.(msg);
      } finally {
        if (!cancelled) setReserving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, deferProjectNumber, assignProjectNumberOnSubmit]);

  const handleClientLabel = (next: string) => {
    onChange({ ...value, clientLabel: next });
  };
  const handleDescriptor = (next: string) => {
    onChange({ ...value, descriptor: next });
  };
  const handleDescriptorBlur = () => {
    const normalized = normalizeDescriptor(value.descriptor);
    if (normalized !== value.descriptor) {
      onChange({ ...value, descriptor: normalized });
    }
  };

  const preview = useMemo(() => {
    const num = value.projectNumber.trim();
    const label = value.clientLabel.trim();
    const desc = value.descriptor.trim();
    if (skipNumber) {
      return [label, desc].filter(Boolean).join(' ').trim() || '—';
    }
    if (!num) return reserving ? 'Reserving project number…' : '—';
    return [num, label, desc].filter(Boolean).join(' ').trim() || num;
  }, [value, reserving, skipNumber]);

  const numberHelp =
    mode === 'edit' && isProjectNumber(value.projectNumber)
      ? 'Immutable once assigned — anchors QuickBooks, files, portal, and P&L.'
      : 'Previewed from the global counter and reserved when you save. Cancel to release.';

  return (
    <div className={`npf-root ${compact ? 'npf-root--compact' : ''} ${disabled ? 'is-disabled' : ''}`}>
      <div className={`npf-naming-grid${compact ? '' : ' npf-naming-grid--stacked'}`}>
      {skipNumber ? (
        <div className="npf-row npf-deferred-number">
          <span className="npf-label">Project Number</span>
          <div className="npf-deferred-note">
            {mode === 'edit'
              ? (assignNumberHint
                || 'Not assigned yet — assign a project number when this job is ready to track.')
              : (deferredNumberNote || 'Will be assigned after design fee payment.')}
          </div>
          {mode === 'edit' && onAssignNumber && (
            <button
              type="button"
              className="npf-assign-btn"
              onClick={onAssignNumber}
              disabled={disabled}
            >
              Assign project number
            </button>
          )}
        </div>
      ) : (
          <div className="npf-row npf-row--number">
            <label
              className="npf-label"
              htmlFor={numberFieldId}
              title={compact ? numberHelp : undefined}
            >
              Project #
              {!compact && (
                <>
                  {' '}
                  <span className="npf-locked-pill" title="Project numbers are immutable once assigned. They anchor every other system (QuickBooks, files, portal, P&L) so they never change.">
                    <Lock size={11} /> locked
                  </span>
                </>
              )}
              {compact && (
                <span className="npf-locked-pill" title={numberHelp}>
                  <Lock size={10} aria-hidden />
                </span>
              )}
            </label>
            <div className="npf-input-wrap">
              <input
                id={numberFieldId}
                type="text"
                className="npf-input npf-input-locked"
                value={reserving ? '' : value.projectNumber}
                placeholder={reserving ? 'Reserving…' : '#NNNN'}
                readOnly
                tabIndex={-1}
              />
              {reserving && (
                <span className="npf-spinner" aria-hidden="true">
                  <Sparkles size={12} />
                </span>
              )}
            </div>
            {!compact && (
              <div className="npf-help">
                {mode === 'edit' && isProjectNumber(value.projectNumber) ? (
                  numberHelp
                ) : (
                  <>Previewed from the global counter (e.g. <code>#0048</code>) and reserved when you save. Cancel to release.</>
                )}
              </div>
            )}
          </div>
      )}

          <div className="npf-row npf-row--label">
            <label
              className="npf-label"
              htmlFor={labelFieldId}
              title={compact ? 'Short identifier clients and PMs see. Defaults from the lead.' : undefined}
            >
              Client label
            </label>
            <input
              id={labelFieldId}
              type="text"
              className="npf-input"
              value={value.clientLabel}
              onChange={e => handleClientLabel(e.target.value)}
              placeholder={compact ? 'Client or company' : 'Last name (residential) or company name (commercial)'}
              disabled={disabled}
              maxLength={60}
            />
            {!compact && (
              <div className="npf-help">
                The short identifier clients and PMs see. Defaults from the lead.
              </div>
            )}
          </div>

          <div className="npf-row npf-row--descriptor">
            <label
              className="npf-label"
              htmlFor={descriptorFieldId}
              title={compact ? 'Structure type — pick a suggestion or type your own.' : undefined}
            >
              Descriptor
            </label>
            <input
              id={descriptorFieldId}
              type="text"
              className="npf-input"
              value={value.descriptor}
              onChange={e => handleDescriptor(e.target.value)}
              onBlur={handleDescriptorBlur}
              placeholder="Pavilion, Barn, Porch…"
              list={datalistId}
              disabled={disabled}
              maxLength={40}
            />
            <datalist id={datalistId}>
              {DESCRIPTOR_SUGGESTIONS.map(s => (
                <option key={s} value={s} />
              ))}
            </datalist>
            {!compact && (
              <div className="npf-help">
                Structure type. Pick a suggestion or type your own.
              </div>
            )}
          </div>
      </div>

      <div className={`npf-preview${compact ? ' npf-preview--compact' : ''}`}>
        <span className="npf-preview-label">Preview</span>
        <span className="npf-preview-name">{preview}</span>
      </div>
    </div>
  );
};

export default NameProjectFields;
